import {
  AccountGroupings,
  ElementsButton,
  Input,
  SegmentedControlAnimated,
  SideOver,
  SideOverFooter,
  SideOverHeader,
  SideOverHeading,
  useSideOverContext,
} from '../components';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { CashAccountType, InvestmentType, LoanType, MappedEntityType } from '../../generated/graphql';
import { LocalThemeContextProvider, useLocalThemeContext } from '../context';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { changeAccountType, usePlaidItemsContext } from '../../Plaid';

interface ChangeAccountTypeSideOverPageProps {
  initialAccountType: LoanType | InvestmentType | CashAccountType;
  initialAccountCategory: MappedEntityType;
  accountID?: string | null;
  plaidItemAccountID: string;
  accountName: string;
  setEditAccountOpen: (opened: boolean) => void;
}

interface ChangeLinkedAccountFormValues {
  accountName: string;
  accountType: CashAccountType | InvestmentType | LoanType;
}

const ChangeLinkedAccountTypeForm = ({
  initialAccountType,
  initialAccountCategory,
  plaidItemAccountID,
  accountName,
}: Omit<ChangeAccountTypeSideOverPageProps, 'setEditAccountOpen'>) => {
  const { closeSideOver } = useSideOverContext();
  const { paletteSeedColor } = useLocalThemeContext();
  const { plaidItems, setPlaidItems } = usePlaidItemsContext();

  const { t: tCommon } = useTranslation('common');
  const { t: tUI } = useTranslation('UI');

  const {
    register,
    handleSubmit,
    watch,
    formState: { isDirty },
    control,
  } = useForm<ChangeLinkedAccountFormValues>({
    defaultValues: {
      accountName,
      accountType: initialAccountType,
    },
  });
  const nickname = watch('accountName');

  const [viewIndex, setViewIndex] = useState<number>(initialAccountCategory == MappedEntityType.Loan ? 1 : 0);
  const [segmentedControlIdx, setSegmentedControlIdx] = useState<number>(
    initialAccountCategory == MappedEntityType.Loan ? 1 : 0
  );

  const onSubmit: SubmitHandler<ChangeLinkedAccountFormValues> = (data) => {
    const currentItem = plaidItems.find((item) => item.plaidItemAccountID === plaidItemAccountID);
    if (currentItem) {
      currentItem.name = data.accountName;
      changeAccountType(currentItem, data.accountType);
      setPlaidItems([...plaidItems]);
      closeSideOver();
    }
  };

  return (
    <LocalThemeContextProvider  paletteSeedColor={paletteSeedColor}>
      <SideOverHeader className="ml-2" />
      <SideOverHeading className="mb-4" heading={tUI('edit-account-header')} />
      <h3 className="text-[18px] text-darkMode-gray-medium font-normal mb-8">{tUI('edit-account-subheader')}</h3>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="pb-[108px]">
          <Input
            defaultValue={accountName}
            className="mb-10"
            {...register('accountName')}
            label={tCommon('account-nickname')}
          />
          <SegmentedControlAnimated
            setItem={setSegmentedControlIdx}
            activeItemIndex={segmentedControlIdx}
            onLayoutAnimationComplete={() => setViewIndex(segmentedControlIdx)}
            items={[tCommon('assets'), tCommon('debts')]}
          />
          <Controller
            name="accountType"
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                {viewIndex === 0 && (
                  <>
                    <AccountGroupings
                      selected={value}
                      setSelected={onChange}
                      title={tCommon('cash-accounts')}
                      items={Object.values(CashAccountType)}
                    />
                    <AccountGroupings
                      selected={value}
                      setSelected={onChange}
                      title={tCommon('investments')}
                      items={Object.values(InvestmentType)}
                    />
                  </>
                )}
                {viewIndex === 1 && (
                  <AccountGroupings
                    selected={value}
                    title={tCommon('debts')}
                    setSelected={onChange}
                    items={Object.values(LoanType)}
                  />
                )}
              </>
            )}
          />
        </div>
        <SideOverFooter className={'!bg-[#EFEFEF] dark:!bg-androidCard'}>
          <ElementsButton
            disabled={!nickname.length || !isDirty}
            icon="check"
            iconClassName="font-icon"
            iconPosition="left"
            label={tCommon('save')}
            size="large"
          />
        </SideOverFooter>
      </form>
    </LocalThemeContextProvider>
  );
};

export const ChangeLinkedAccountTypeSideOverPage = ({
    initialAccountType,
    initialAccountCategory,
    plaidItemAccountID,
    accountName,
    setEditAccountOpen,
  }: ChangeAccountTypeSideOverPageProps) => {
  return (
    <SideOver closeWithoutNav={true} onExitComplete={() => setEditAccountOpen(false)}>
      <ChangeLinkedAccountTypeForm
        initialAccountType={initialAccountType}
        initialAccountCategory={initialAccountCategory}
        plaidItemAccountID={plaidItemAccountID}
        accountName={accountName}
      />
    </SideOver>
  )
}
